import React, { useEffect, useState } from "react";
import { Row, Col, Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import antivirusService from "../../services/antivirusService";

const card = {
  background: "var(--vz-secondary-bg, #fff)",
  border: "1px solid var(--vz-border-color)",
  borderRadius: 10,
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const ProtectionStatusCard = () => {
  const [stats,   setStats]   = useState(null);
  const [scans,   setScans]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
    const i = setInterval(load, 30000);
    return () => clearInterval(i);
  }, []);

  const load = async () => {
    try {
      const [statsRes, scansRes] = await Promise.all([
        antivirusService.getStatistics(),
        antivirusService.getAgentScans()
      ]);
      if (statsRes.success) setStats(statsRes.statistics);
      if (scansRes.success) setScans(scansRes.scans || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const agentsScanned    = new Set(scans.map(s => s.agent_id)).size;
  const agentsWithThreats = stats?.agents_with_threats || 0;
  const agentsClean      = agentsScanned - agentsWithThreats;
  const totalThreats     = stats?.total_threats || 0;
  const coverage         = agentsScanned > 0 ? ((agentsClean / agentsScanned) * 100).toFixed(1) : "100.0";
  const isSecure         = agentsWithThreats === 0;

  const secureColor = isSecure ? "var(--vz-success)" : "var(--vz-danger)";
  const secureBg    = isSecure ? "rgba(var(--vz-success-rgb),.1)" : "rgba(var(--vz-danger-rgb),.1)";
  const secureBorder = isSecure ? "rgba(var(--vz-success-rgb),.3)" : "rgba(var(--vz-danger-rgb),.3)";

  const statBox = (label, value, color) => (
    <div style={{
      padding: "10px 12px", borderRadius: 8, textAlign: "center",
      background: "rgba(var(--vz-primary-rgb),.04)",
      border: "1px solid var(--vz-border-color)"
    }}>
      <p style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 700, color: "var(--vz-secondary-color)", margin: "0 0 4px" }}>
        {label}
      </p>
      <p style={{ fontSize: 20, fontWeight: 800, color: color || "var(--vz-body-color)", margin: 0 }}>
        {loading ? "—" : value}
      </p>
    </div>
  );

  return (
    <div style={{ ...card, height: "100%" }}>
      {/* Header */}
      <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--vz-border-color)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "1px", fontWeight: 700, color: "var(--vz-secondary-color)" }}>
          Protection Status
        </span>
        <span style={{
          display: "inline-flex", alignItems: "center", gap: 5,
          padding: "3px 12px", borderRadius: 20, fontSize: 11, fontWeight: 600,
          color: secureColor, background: secureBg, border: `1px solid ${secureBorder}`
        }}>
          <FeatherIcon icon={isSecure ? "shield" : "shield-off"} size={11} />
          {isSecure ? "Protected" : "At Risk"}
        </span>
      </div>

      <div style={{ padding: "20px 18px" }}>
        {/* Shield + Coverage */}
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{
            width: 80, height: 80, borderRadius: "50%",
            background: secureBg, border: `2px solid ${secureBorder}`,
            color: secureColor,
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 12px"
          }}>
            <FeatherIcon icon="shield" size={32} />
          </div>
          {loading ? <Spinner size="sm" color="success" /> : (
            <>
              <p style={{ fontSize: 28, fontWeight: 800, color: "var(--vz-body-color)", margin: "0 0 2px" }}>{coverage}%</p>
              <p style={{ color: "var(--vz-secondary-color)", fontSize: 12, margin: 0 }}>Endpoint Protection Coverage</p>
            </>
          )}
        </div>

        <hr style={{ borderColor: "var(--vz-border-color)", margin: "0 0 16px" }} />

        {/* Stats grid */}
        <Row className="g-2">
          <Col xs={6}>{statBox("Endpoints Scanned", agentsScanned, "var(--vz-primary)")}</Col>
          <Col xs={6}>{statBox("Threats Found",     totalThreats,    totalThreats > 0 ? "var(--vz-danger)" : "var(--vz-success)")}</Col>
          <Col xs={6}>{statBox("Clean Endpoints",   agentsClean,     "var(--vz-success)")}</Col>
          <Col xs={6}>{statBox("Affected Endpoints", agentsWithThreats, agentsWithThreats > 0 ? "var(--vz-danger)" : "var(--vz-success)")}</Col>
        </Row>

        {/* DB status */}
        <div style={{
          marginTop: 16, paddingTop: 14, display: "flex", justifyContent: "space-between", alignItems: "center",
          borderTop: "1px solid var(--vz-border-color)"
        }}>
          <span style={{ fontSize: 11, color: "var(--vz-secondary-color)", display: "flex", alignItems: "center", gap: 5 }}>
            <FeatherIcon icon="database" size={11} style={{ color: "var(--vz-info)" }} />Threat Database
          </span>
          <span style={{ fontSize: 11, color: "var(--vz-secondary-color)", display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--vz-success)", display: "inline-block" }} />
            Live · v1.4
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProtectionStatusCard;
